/**
 * AuditLogs Page (Organization-Scoped)
 * 
 * Shows audit logs for the current organization only.
 * Available to organization admins (owner, admin roles).
 * 
 * For system-wide audit logs (super admins only), see /admin/audit-logs
 */

import React, { useState, useEffect } from 'react';
import type { AuditLog, AuditAction, AuditResourceType } from '@simcheck/shared-types';
import { Shield, Search, Filter, Calendar, User, FileText, AlertCircle } from 'lucide-react';
import { AppShell } from '../components/layout/AppShell';
import { DataTable, Badge } from '@/components/ui';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useUserTimezone } from '../hooks/useDateFormat';
import { getOrganizationAuditLogs } from '../services/auditLogService';

export const AuditLogs: React.FC = () => {
  const { activeOrganization, userRole, isSuperAdmin } = useAuth();
  const tz = useUserTimezone();
  const { error: showError } = useToast();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAction, setSelectedAction] = useState<AuditAction | 'all'>('all');
  const [selectedResource, setSelectedResource] = useState<AuditResourceType | 'all'>('all');

  // Only organization admins (owner, admin) can view org audit logs
  const canViewOrgLogs = userRole === 'owner' || userRole === 'admin';

  useEffect(() => {
    if (activeOrganization && canViewOrgLogs) {
      loadAuditLogs();
    }
  }, [activeOrganization, canViewOrgLogs]);

  const loadAuditLogs = async () => {
    if (!activeOrganization) return;

    try {
      setLoading(true);
      // GET /audit-logs/organization/:id — org-scoped and RBAC'd server-side
      // (system role, or owner/admin of this org). This page used to stub out
      // an empty array behind a TODO whose backend had already shipped, so it
      // showed every tenant an empty table forever.
      const data = await getOrganizationAuditLogs(activeOrganization.id, { limit: 200 });
      setLogs(data);
    } catch (error: any) {
      console.error('Failed to load audit logs:', error);
      showError(error.message || 'Failed to load audit logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp: Date | string) => {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: tz,
    }).format(date);
  };

  const getActionBadgeColor = (action: AuditAction) => {
    if (action.includes('create')) return 'success';
    if (action.includes('delete')) return 'destructive';
    if (action.includes('update')) return 'warning';
    if (action.includes('login') || action.includes('logout')) return 'secondary';
    return 'default';
  };

  const getSuccessBadge = (success: boolean) => {
    return success ? (
      <Badge variant="success">Success</Badge>
    ) : (
      <Badge variant="destructive">Failed</Badge>
    );
  };

  const filteredLogs = logs.filter((log) => {
    const matchesSearch = 
      log.actorEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.resource?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesAction = selectedAction === 'all' || log.action === selectedAction;
    const matchesResource = selectedResource === 'all' || log.resourceType === selectedResource;

    return matchesSearch && matchesAction && matchesResource;
  });

  const columns = [
    {
      key: 'timestamp',
      header: 'Time',
      sortable: true,
      render: (_: any, log: AuditLog) => (
        <span className="text-sm text-muted-foreground">
          {formatDate(log.timestamp)}
        </span>
      ),
    },
    {
      key: 'actorEmail',
      header: 'Actor',
      sortable: true,
      render: (_: any, log: AuditLog) => (
        <div className="flex items-center space-x-2">
          <User className="w-4 h-4 text-muted-foreground/70" />
          <div>
            <p className="text-sm font-medium text-foreground">
              {log.actorEmail || 'Unknown'}
            </p>
            {log.actorSystemRole && (
              <p className="text-xs text-muted-foreground/70">
                {log.actorSystemRole}
              </p>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'action',
      header: 'Action',
      sortable: true,
      render: (_: any, log: AuditLog) => (
        <Badge variant={getActionBadgeColor(log.action)}>
          {log.action}
        </Badge>
      ),
    },
    {
      key: 'resourceType',
      header: 'Resource',
      sortable: true,
      render: (_: any, log: AuditLog) => (
        <div>
          <p className="text-sm font-medium text-foreground">
            {log.resourceType || 'N/A'}
          </p>
          {log.resource && (
            <p className="text-xs text-muted-foreground/70 font-mono">
              {log.resource.substring(0, 20)}...
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'success',
      header: 'Status',
      sortable: true,
      render: (_: any, log: AuditLog) => getSuccessBadge(log.success),
    },
  ];

  if (!canViewOrgLogs) {
    return (
      <AppShell>
        <div className="flex items-center justify-center h-full">
          <div className="text-center max-w-md">
            <AlertCircle className="w-16 h-16 text-status-error mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-2">
              Access Denied
            </h2>
            <p className="text-muted-foreground mb-4">
              You need to be an organization admin (owner or admin role) to view audit logs.
            </p>
            <p className="text-sm text-muted-foreground/70">
              Current role: <Badge>{userRole || 'none'}</Badge>
            </p>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-primary-100 dark:bg-primary-900/30">
                <Shield className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">
                  Audit Logs
                </h1>
                <p className="text-sm text-muted-foreground">
                  Security and compliance audit trail for {activeOrganization?.name}
                </p>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/70" />
              <input
                type="text"
                placeholder="Search by actor, action, or resource..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg
                         bg-card
                         border border-border
                         text-foreground
                         placeholder:text-muted-foreground/70
                         focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Action Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/70" />
              <select
                value={selectedAction}
                onChange={(e) => setSelectedAction(e.target.value as AuditAction | 'all')}
                className="w-full pl-10 pr-4 py-2 rounded-lg
                         bg-card
                         border border-border
                         text-foreground
                         focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Actions</option>
                <option value="user.create">User Created</option>
                <option value="user.update">User Updated</option>
                <option value="user.delete">User Deleted</option>
                <option value="flow.execute">Flow Executed</option>
                <option value="modem.assign">Modem Assigned</option>
              </select>
            </div>

            {/* Resource Type Filter */}
            <div className="relative">
              <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/70" />
              <select
                value={selectedResource}
                onChange={(e) => setSelectedResource(e.target.value as AuditResourceType | 'all')}
                className="w-full pl-10 pr-4 py-2 rounded-lg
                         bg-card
                         border border-border
                         text-foreground
                         focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Resources</option>
                <option value="User">Users</option>
                <option value="Organization">Organizations</option>
                <option value="Flow">Flows</option>
                <option value="Modem">Modems</option>
                <option value="Execution">Executions</option>
              </select>
            </div>
          </div>

          {/* Info Banner - Only for super admins */}
          {isSuperAdmin && (
            <div className="mt-4 p-3 rounded-lg bg-accent-blue/10 border border-accent-blue/30">
              <div className="flex items-center space-x-2">
                <FileText className="w-4 h-4 text-accent-blue" />
                <span className="text-sm text-accent-blue">
                  Viewing organization-scoped logs. For system-wide logs, visit{' '}
                  <a href="/admin/audit-logs" className="underline font-medium">
                    /admin/audit-logs
                  </a>
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden p-6">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
                <p className="text-muted-foreground">
                  Loading audit logs...
                </p>
              </div>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center max-w-md">
                <Shield className="w-16 h-16 text-muted-foreground/70 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  No Audit Logs Found
                </h3>
                <p className="text-muted-foreground">
                  {searchTerm || selectedAction !== 'all' || selectedResource !== 'all'
                    ? 'No logs match your current filters. Try adjusting your search criteria.'
                    : 'No audit logs have been recorded for this organization yet.'}
                </p>
              </div>
            </div>
          ) : (
            <DataTable
              data={filteredLogs}
              columns={columns}
              keyExtractor={(log) => log.id || `log-${Math.random()}`}
              searchable
              searchPlaceholder="Search by actor, action, or resource..."
              paginated
              defaultPageSize={25}
              emptyMessage="No audit logs found"
            />
          )}
        </div>
      </div>
    </AppShell>
  );
};

